import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { NumberFormatter } from "react-number-formatter";
import { DarkGrey } from "../../shared/styles/colors";
import { Spinner } from "../../components";
import Client from "../../model/client";
import US_States from "../../model/enum/states.us.enum";
import { ClientServices } from "../../model/enum/client-services.enum";
import DeleteModal from "../modal/DeleteModal";
import EditIcon from "../../assets/admin/edit-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import ApiService from "../../service/ApiService";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import AlertService from "../../service/AlertService";
import ToastMessages from "../../model/enum/toast-messages.enum";
import ClientLevel from "../../model/clientLevel";
import ClientType from "../../model/client-type";
import useStyles from "./styles";
import Misc from "../../utils/Misc";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

const ClientModal = ({
  client,
  resetList,
  externalService,
  setClient,
}: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = client != null;
  const id = edit ? client.id : 0;
  const [firstName, setFirstName] = useState(edit ? client.firstName : null);
  const [lastName, setLastName] = useState(edit ? client.lastName : null);
  const [email, setEmail] = useState(edit ? client.email : null);
  const [phone, setPhone] = useState(edit ? client.phone : null);
  const [zipCode, setZipCode] = useState(
    edit && client.zip ? client?.zip : null
  );
  const [street, setStreet] = useState(
    edit && client.street ? client.street : null
  );
  const [city, setCity] = useState(edit && client.city ? client.city : null);
  const [state, setState] = useState(
    edit && client.state ? client.state : null
  );
  const [level, setLevel] = useState(edit ? client.ClientLevel : null);
  const [levels, setLevels] = useState<ClientLevel[]>([]);
  const [type, setType] = useState(edit ? client.ClientType : null);
  const [types, setTypes] = useState<ClientType[]>([]);
  const [altName, setAltName] = useState(
    edit && client.alternativeName != "" ? client.alternativeName : null
  );
  const [altPhone, setAltPhone] = useState(
    edit && client.alternativePhone != "" ? client.alternativePhone : null
  );
  const [altEmail, setAltEmail] = useState(
    edit && client.alternativeEmail != "" ? client.alternativeEmail : null
  );
  const [notes, setNotes] = useState(edit ? client.notes : null);
  const [loading, setLoading] = useState(true);

  const US_State = new US_States();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [clientLevels, clientTypes] = await Promise.all([
          ApiService.get<ClientLevel[]>(
            `${DatabaseEntities.CLIENTS}/${DatabaseEntities.CLIENT_LEVELS}`
          ),
          ApiService.get<ClientType[]>(
            `${DatabaseEntities.CLIENTS}/${DatabaseEntities.CLIENT_TYPES}`
          ),
        ]);
        setLevels(clientLevels);
        setTypes(clientTypes);
      } catch (error) {
        AlertService.error(ToastMessages.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };
    if (open && (Misc.isArrayEmpty(levels) || Misc.isArrayEmpty(types)))
      fetchData();
  }, [open, levels, types]);

  const handleFirstName = (e: any) => setFirstName(e.target.value);
  const handleLastName = (e: any) => setLastName(e.target.value);
  const handleEmail = (e: any) => setEmail(e.target.value);
  const handlePhone = (e: any) => setPhone(e);
  const handleStreet = (e: any) => setStreet(e.target.value);
  const handleCity = (e: any) => setCity(e.target.value);
  const handleState = (e: any) => setState(e.target.value);
  const handleAltName = (e: any) => setAltName(e.target.value);
  const handleAltPhone = (e: any) => setAltPhone(e);
  const handleAltEmail = (e: any) => setAltEmail(e.target.value);
  const handleNotes = (e: any) => setNotes(e.target.value);

  const handleZipCode = (e: any) => {
    e.preventDefault();
    let zip = e.target.value;
    if (!isNaN(zip)) {
      if (US_State.isZipValid(zip)) {
        setCity(US_State.getStateCity(zip));
        setState(US_State.getStateName(zip));
      } else {
        setCity("");
        setState("");
      }
      setZipCode(zip);
    }
  };

  const handleLevel = (e: any) => {
    let value = levels.find((lvl: ClientLevel) => lvl.id == e.target.value);
    setLevel(value);
  };

  const handleType = (e: any) => {
    let value = types.find((tp: ClientType) => tp.id == e.target.value);
    setType(value);
  };

  const handleCreate = async (payload: Client) => {
    await ApiService.post<Client>(DatabaseEntities.CLIENTS, payload)
      .then((data: any) => {
        if (data.existingClient) {
          AlertService.error(data.error);
          return;
        }
        AlertService.success(ToastMessages.CREATED);
        resetList?.();
      })
      .catch((error: any) => {
        AlertService.error(`${ToastMessages.CREATE_FAILED}: ${error.message}`);
      });
  };

  const handleUpdate = async (payload: Client) => {
    await ApiService.put<Client>(
      `${DatabaseEntities.CLIENTS}/${payload.id}`,
      payload
    )
      .then((data: any) => {
        if (data.existingClient) {
          AlertService.error(data.error);
          return;
        }

        AlertService.success(ToastMessages.UPDATED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Client) => {
    await ApiService.delete<any>(`${DatabaseEntities.CLIENTS}/${payload.id}`)
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const invalidInput = () => {
    return (
      firstName === "" ||
      email === "" ||
      email?.indexOf("@") === -1 ||
      email?.indexOf(".") === -1 ||
      phone === ""
    );
  };

  const register = async () => {
    if (invalidInput()) return;

    const payload: Client = {
      id: id,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email,
      phone: phone,
      street: street,
      zip: zipCode,
      city: city,
      state: state,
      alternativeName: altName,
      alternativePhone: altPhone,
      alternativeEmail: altEmail,
      clientLevelId: level ? level.id : null,
      clientTypeId: type ? type.id : null,
      notes: notes,
      service: ClientServices.MANUAL,
    };

    if (!externalService) {
      if (edit) await handleUpdate(payload);
      else await handleCreate(payload);
    } else {
      setLoading(true);
      await handleCreate(payload);
      setClient(payload);
      setLoading(false);
    }

    handleClose();
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : !externalService ? (
        <Btn
          className={classes.headerButton}
          variant="contained"
          onClick={handleOpen}
        >
          + Add Client
        </Btn>
      ) : (
        <Btn className={classes.externalButton} onClick={handleOpen}>
          Add New
        </Btn>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        <Box sx={style}>
          <div className={classes.headerDiv}>
            {edit ? (
              <DeleteModal item={client} handleDelete={handleDelete} />
            ) : (
              <Typography className={classes.title}>Add client</Typography>
            )}
            <Button onClick={handleClose} sx={{ color: "black" }}>
              <CloseIcon />
            </Button>
          </div>
          <hr />
          {loading && <Spinner />}
          <div className={classes.form}>
            <div className={classes.inputGroup}>
              <input
                className={classes.input}
                value={firstName}
                onChange={handleFirstName}
                placeholder="First Name"
              />
              <input
                className={classes.input}
                value={lastName}
                onChange={handleLastName}
                placeholder="Last Name"
              />
            </div>
            <div className={classes.inputGroup}>
              <input
                className={classes.input}
                value={email}
                type="email"
                onChange={handleEmail}
                placeholder="Email"
              />
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.input}>
                <NumberFormatter
                  defaultCountry="USA"
                  placeholder="Phone"
                  value={phone}
                  getValue={(n: string) => handlePhone(n)}
                />
              </div>
            </div>
            <div className={classes.inputGroup}>
              <input
                className={classes.input}
                value={street}
                onChange={handleStreet}
                placeholder="Street"
              />
              <input
                className={classes.input}
                value={zipCode}
                onChange={handleZipCode}
                placeholder="ZIP code"
              />
            </div>
            <div className={classes.inputGroup}>
              <input
                className={classes.input}
                value={city}
                onChange={handleCity}
                placeholder="City"
              />
              <input
                className={classes.input}
                value={state}
                onChange={handleState}
                placeholder="State"
              />
            </div>
            <div className={classes.inputGroup}>
              <select
                className={classes.input}
                onChange={handleType}
                value={type?.id}
              >
                <option value="">Select type</option>
                {types.map((tp: ClientType) => {
                  return (
                    <option key={tp.id} value={tp.id}>
                      {tp.name}
                    </option>
                  );
                })}
              </select>
              <select
                className={classes.input}
                onChange={handleLevel}
                value={level?.id}
              >
                <option value="" style={{ color: DarkGrey }}>
                  Select level
                </option>
                {levels.map((lvl: ClientLevel) => {
                  return (
                    <option key={lvl.id} value={lvl.id}>
                      {lvl.name}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className={classes.inputGroup}>
              <input
                className={classes.input}
                value={altName}
                onChange={handleAltName}
                placeholder="Alternative Name"
              />
              <div className={classes.input}>
                <NumberFormatter
                  defaultCountry="USA"
                  placeholder="Alternative Phone"
                  value={altPhone}
                  getValue={(n: string) => handleAltPhone(n)}
                />
              </div>
            </div>
            <div className={classes.inputGroup}>
              <input
                className={classes.input}
                value={altEmail}
                onChange={handleAltEmail}
                placeholder="Alternative Email"
              />
            </div>
            <div className={classes.inputGroup}>
              <textarea
                className={classes.textarea}
                placeholder="Add some notes..."
                onChange={handleNotes}
                value={notes}
              />
            </div>
          </div>
          <div className={classes.buttonDiv}>
            <Btn
              className={classes.button}
              onClick={() => register()}
              disabled={invalidInput()}
            >
              {edit ? "Save" : "Create"}
            </Btn>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default ClientModal;
